import { useEffect, useMemo, useCallback, useState } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/store";
import {
  Alert,
  Box,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Button,
  Skeleton,
  CircularProgress,
  Backdrop,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router";
import {
  ChevronRight as ChevronRightIcon,
  FolderZip as FolderZipIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import type { Customer } from "../../../types/api";

import { TABS } from "./constants";
import {
  fetchInvoices,
  selectInvoice,
} from "../../../redux/slices/invoicesSlice";
import { openProjectDrawer } from "../../../redux/slices/projectsSlice";
import { fetchTransactions } from "../../../redux/slices/transactionsSlice";
import {
  fetchProjectSummary,
  resetReporting,
} from "../../../redux/slices/reportingSlice";
import {
} from "../../../redux/slices/assetsSlice";

import InvoiceDetailsDrawer from "../components/PendingInvoices/InvoiceDetailsDrawer";
import CartPaymentDialog from "../components/PendingInvoices/CartPaymentDialog";
import CartFab from "../components/PendingInvoices/CartFab";

// ─── Helpers ─────────────────────────────────────────────────────────
const fmtCurrency = (val: number | null | undefined, compact = false) => {
  if (val == null) return "$0";
  if (compact && Math.abs(val) >= 1_000_000)
    return `$${(val / 1_000_000).toFixed(1)}M`;
  if (compact && Math.abs(val) >= 1_000) return `$${(val / 1_000).toFixed(0)}k`;
  return val.toLocaleString("en-US", { style: "currency", currency: "USD" });
};

const fmtDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "";
  try {
    return format(parseISO(dateStr), "MMM dd, yyyy");
  } catch {
    return dateStr;
  }
};

// Amount left to pay on an invoice, matching what the cart charge sends.
const invoicePayable = (invoice: any) =>
  invoice?.calculatedAmountDue ??
  (invoice?.amountDue != null && invoice?.scheduledAmount != null
    ? invoice.amountDue - invoice.scheduledAmount
    : (invoice?.amount ?? 0));

const maskBankAccount = (value: unknown) => {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length >= 4 ? `•••• ${digits.slice(-4)}` : "Account on file";
};

const bankAccountType = (account: any) =>
  `${account?.isSavings ? "Savings" : "Checking"} · ${
    account?.isPersonalAcct ? "Personal" : "Business"
  }`;

const normalizeProjectName = (projectName: string | null | undefined) =>
  projectName?.trim().toLowerCase() || "";

const txnMethod = (txn: any) =>
  txn?.receivablesType ||
  (txn?.fundingAccount?.type === "CARD_ACCOUNT" ? "Card" : "ACH");

const filterInputStyle = {
  height: 32,
  border: "1px solid var(--tpc-300)",
  borderRadius: 0,
  padding: "0 8px",
  fontFamily: '"Brandon Grotesque", sans-serif',
  fontSize: 12,
  background: "#ffffff",
};

const getInvoiceSortTimestamp = (invoice: any) => {
  const dateCandidates = [
    invoice?.invoiceDate,
    invoice?.createdTime,
    invoice?.updatedTime,
    invoice?.dueDate,
  ];

  for (const value of dateCandidates) {
    if (typeof value !== "string" || value.trim() === "") continue;

    const parsedWithDate = Date.parse(value);
    if (!Number.isNaN(parsedWithDate)) return parsedWithDate;

    try {
      const parsedIso = parseISO(value).getTime();
      if (!Number.isNaN(parsedIso)) return parsedIso;
    } catch {
      continue;
    }
  }

  return 0;
};

const FINANCIAL_LINKS = [
  { key: "invoices-budgets", label: "Invoices & Budgets" },
  { key: "contracts", label: "Contracts" },
  { key: "plans", label: "Plans & Drawings" },
  { key: "permits", label: "Permits & Reports" },
  { key: "schedules", label: "Schedules" },
];

// Status maps
const STATUS_LABEL: Record<string, string> = {
  "0": "Paid",
  "1": "Open",
  "2": "Partial",
  "4": "Scheduled",
  processing: "Processing",
};
const STATUS_BG: Record<string, string> = {
  "0": "var(--surface-container, #efefef)",
  "1": "var(--tertiary-container, #efefef)",
  "2": "var(--surface-container-high, #efefef)",
  "4": "var(--surface-container-highest, #dcdcdc)",
  processing: "var(--tertiary-container, #efefef)",
};
const STATUS_COLOR: Record<string, string> = {
  "0": "var(--on-surface, #131517)",
  "1": "var(--on-tertiary-container, #2a2b2d)",
  "2": "var(--on-surface-variant, #2a2b2d)",
  "4": "var(--on-surface-variant, #2a2b2d)",
  processing: "var(--on-tertiary-container, #2a2b2d)",
};
const TXN_STATUS_LABEL: Record<string, string> = {
  "0": "Paid",
  "1": "Void",
  "2": "Scheduled",
  "3": "Canceled",
};

// Reusable expand/collapse toggle
const ExpandToggle = ({
  expanded,
  onClick,
  totalCount,
  visibleCount,
}: {
  expanded: boolean;
  onClick: () => void;
  totalCount: number;
  visibleCount: number;
}) => {
  if (totalCount <= visibleCount) return null;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        py: 1.5,
        cursor: "pointer",
        transition: "background 0.15s ease",
        "&:hover": { backgroundColor: "var(--surface-container-low, #f7f7f7)" },
        borderRadius: 0,
      }}
    >
      {expanded ? (
        <CollapseIcon sx={{ fontSize: 16, color: "var(--outline, #737373)" }} />
      ) : (
        <ExpandIcon sx={{ fontSize: 16, color: "var(--outline, #737373)" }} />
      )}
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontSize: "0.5rem",
          fontWeight: 700,
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          color: "var(--outline, #737373)",
        }}
      >
        {expanded ? "Show less" : `Show all ${totalCount}`}
      </Typography>
    </Box>
  );
};

// =====================================================================
const FinancialDashboard = () => {
  const dispatch = useAppDispatch();

  const { invoices, fetchInvoicesStatus } = useAppSelector(
    (state) => state.invoices,
  );
  const { transactions, fetchTransactionsStatus } = useAppSelector(
    (state) => state.transactions,
  );
  const { activeTab, projects, fetchProjectsStatus, selectedProject } =
    useAppSelector((state) => state.projects);
  // const {
  //   projectSummary,
  //   projectSummaryStatus,
  // } = useAppSelector((state) => state.reporting);

  const [invoiceFilter, setInvoiceFilter] = useState<string>("1");
  const [selectedTxn, setSelectedTxn] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [txnStatus, setTxnStatus] = useState("all");

  // Expand states
  const [invoicesExpanded, setInvoicesExpanded] = useState(false);

  // ── Multi-invoice ("shopping cart") selection ──
  const [cartIds, setCartIds] = useState<string[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const { cartChargeStatus, cartChargeError } = useAppSelector(
    (state) => state.invoices,
  ) as any;
  const { enqueueSnackbar } = useSnackbar();

  const toggleCart = useCallback((invoiceId: string) => {
    setCartIds((prev) =>
      prev.includes(invoiceId)
        ? prev.filter((id) => id !== invoiceId)
        : [...prev, invoiceId],
    );
  }, []);

  const PREVIEW_COUNT = 2;

  // ── Project selection from sidebar ────────────────────────────────
  // Fall back to first project while Redux hydrates.
  const displayProject = (selectedProject as Customer) ?? projects?.[0] ?? null;
  const bankAccounts = Array.isArray((displayProject as any)?.bankAccounts)
    ? (displayProject as any).bankAccounts
    : [];

  // Effect 1: account-level data — runs once on mount, independent of project selection
  useEffect(() => {
    dispatch(fetchInvoices({}));
    dispatch(fetchTransactions());
    return () => {
      dispatch(resetReporting());
    };
  }, [dispatch]);

  // Effect 2: project-specific reporting — deferred until Projects.tsx has resolved the
  // projects list. Without this guard, displayProject?.name starts as undefined (empty
  // Redux state) then changes once Projects.tsx's fetchProjects() completes, causing a
  // second round of all three reporting requests.
  useEffect(() => {
    if (fetchProjectsStatus !== "succeeded" && fetchProjectsStatus !== "failed")
      return;
    dispatch(fetchProjectSummary(displayProject?.name));
  }, [dispatch, fetchProjectsStatus, displayProject?.name]);

  // Metrics
  // const totalBilled = useMemo(() => {
  //   if (!projectSummary) return 0;
  //   return projectSummary.current_budget - projectSummary.rem_to_bill;
  // }, [projectSummary]);

  // const billedPercentage = useMemo(() => {
  //   if (!projectSummary || projectSummary.current_budget === 0) return 0;
  //   return Math.round((totalBilled / projectSummary.current_budget) * 100);
  // }, [projectSummary, totalBilled]);

  // const approvedChanges = useMemo(() => {
  //   if (!projectSummary) return 0;
  //   return projectSummary.owner_changes + projectSummary.builder_changes;
  // }, [projectSummary]);

  const displayInvoices = useMemo(() => {
    if (!invoices) return [];

    const scopedInvoices = invoices
      .filter((inv: any) => {
        // Wait for the projects list to resolve: until then there is no
        // project to scope to, and showing every invoice is wrong.
        if (!displayProject) return false;

        const invoiceProjectId = inv.projectId || inv.databaseId;
        const invoiceProjectName = normalizeProjectName(
          inv.projectName || inv.customerName,
        );

        return (
          invoiceProjectId === displayProject.id ||
          invoiceProjectName === normalizeProjectName(displayProject.name)
        );
      })
      .filter((inv: any) => {
        // A zero-dollar row is a placeholder, not an invoice to act on.
        const amount = inv?.amount ?? inv?.amountDue ?? inv?.calculatedAmountDue;
        return amount == null || Number(amount) !== 0;
      })
      .filter((inv: any) => {
        const needle = searchTerm.trim().toLowerCase();
        if (needle) {
          const haystack = [
            inv?.invoiceNumber,
            inv?.projectName,
            inv?.customerName,
            inv?.description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        const timestamp = getInvoiceSortTimestamp(inv);
        if (fromDate && timestamp < Date.parse(fromDate)) return false;
        if (toDate && timestamp > Date.parse(toDate) + 86_400_000 - 1) {
          return false;
        }
        return true;
      })
      .map((inv: any) => ({
        ...inv,
        projectName:
          inv.projectName || inv.customerName || displayProject?.name || "",
      }))
      .sort(
        (left: any, right: any) =>
          getInvoiceSortTimestamp(right) - getInvoiceSortTimestamp(left),
      );

    if (invoiceFilter === "all") return scopedInvoices;
    return scopedInvoices.filter(
      (inv: any) => String(inv.paymentStatus) === invoiceFilter,
    );
  }, [
    invoices,
    invoiceFilter,
    displayProject,
    searchTerm,
    fromDate,
    toDate,
  ]);

  const selectableInvoices = useMemo(
    () =>
      displayInvoices.filter(
        (inv: any) =>
          String(inv.paymentStatus) === "1" &&
          !inv.paymentProcessing &&
          invoicePayable(inv) > 0,
      ),
    [displayInvoices],
  );

  const selectedVisibleCount = useMemo(
    () => selectableInvoices.filter((inv: any) => cartIds.includes(inv.id)).length,
    [selectableInvoices, cartIds],
  );
  const allSelected =
    selectableInvoices.length > 0 &&
    selectedVisibleCount === selectableInvoices.length;
  const someSelected = selectedVisibleCount > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      const visible = new Set(selectableInvoices.map((inv: any) => inv.id));
      setCartIds((prev) => prev.filter((id) => !visible.has(id)));
      return;
    }
    setCartIds((prev) =>
      Array.from(
        new Set([...prev, ...selectableInvoices.map((inv: any) => inv.id)]),
      ),
    );
  };

  const cartInvoices = useMemo(
    () => displayInvoices.filter((inv: any) => cartIds.includes(inv.id)),
    [displayInvoices, cartIds],
  );

  const cartTotal = useMemo(
    () =>
      cartInvoices.reduce(
        (sum: number, inv: any) => sum + (invoicePayable(inv) || 0),
        0,
      ),
    [cartInvoices],
  );

  const displayTransactions = useMemo(() => {
    if (!transactions) return [];
    if (!displayProject) return transactions;

    const needle = searchTerm.trim().toLowerCase();
    return transactions
      .filter((txn: any) => {
        const txnProjectId = txn?.projectId;
        const txnProjectName = txn?.projectName;

        return (
          txnProjectId === displayProject.id ||
          normalizeProjectName(txnProjectName) ===
            normalizeProjectName(displayProject.name)
        );
      })
      .filter((txn: any) => {
        if (txnStatus !== "all" && String(txn?.status) !== txnStatus) {
          return false;
        }
        if (needle) {
          const haystack = [
            txn?.projectName,
            txn?.invoiceNumber,
            txn?.referenceNumber,
            txn?.description,
            txn?.receivablesType,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(needle)) return false;
        }
        const timestamp = Date.parse(txn?.paymentDate || txn?.createdTime || "");
        if (fromDate && (!Number.isFinite(timestamp) || timestamp < Date.parse(fromDate))) {
          return false;
        }
        if (
          toDate &&
          (!Number.isFinite(timestamp) ||
            timestamp > Date.parse(toDate) + 86_400_000 - 1)
        ) {
          return false;
        }
        return true;
      });
  }, [transactions, displayProject, searchTerm, fromDate, toDate, txnStatus]);

  useEffect(() => {
    setInvoicesExpanded(false);
    setCartIds([]); // cart is per-project/filter, so reset on switch
  }, [displayProject?.id, invoiceFilter, activeTab]);

  // Each tab has its own rows; a date range typed for invoices should not
  // silently empty the transactions table when the client switches tabs.
  useEffect(() => {
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setTxnStatus("all");
  }, [activeTab]);

  // Cart payment outcome: on success clear the cart and refresh invoices; on
  // failure surface the backend error.
  useEffect(() => {
    if (cartChargeStatus === "succeeded") {
      enqueueSnackbar("Payment submitted — pending ACH settlement", { variant: "success" });
      setCartOpen(false);
      setCartIds([]);
      dispatch(fetchInvoices({}));
    }
    if (cartChargeStatus === "failed") {
      enqueueSnackbar(cartChargeError, { variant: "error" });
    }
  }, [cartChargeStatus, cartChargeError, dispatch, enqueueSnackbar]);

  const navigate = useNavigate();
  const handleLibraryLinkClick = useCallback(
    (link: { key: string }) => {
      navigate(`/project-home/documents?section=${encodeURIComponent(link.key)}`);
    },
    [navigate],
  );

  const handleInvoiceClick = useCallback(
    (invoice: any) => {
      dispatch(selectInvoice(invoice));
    },
    [dispatch],
  );

  const isSyncing =
    fetchInvoicesStatus === "loading" || fetchTransactionsStatus === "loading";

  // =====================================================================
  return (
    <Box sx={{ pb: cartInvoices.length > 0 ? "96px" : 0 }}>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSyncing}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ ml: 1.5, fontSize: "0.85rem" }}>
          Synchronizing with payment gateway...
        </Typography>
      </Backdrop>

      {/* ─── Main 2-Column Grid ─────────────────────────────────────── */}
      <Box
        sx={{
          py: { xs: 2, md: 3 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "5fr 3fr" },
          gap: { xs: 4, lg: 5 },
          alignItems: "start",
        }}
      >
        {/* ══════ LEFT COLUMN ══════ */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* ═══ Recent Invoices ═══ */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <h2 className="ui-section-label">
                {activeTab === TABS.INVOICES
                  ? "Recent invoices"
                  : activeTab === TABS.TRANSACTIONS
                    ? "Transactions"
                    : "Bank details"}
              </h2>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                {/* Status filters */}
                {activeTab === TABS.INVOICES && (
                  <Box
                    sx={{
                      display: { xs: "none", md: "flex" },
                      backgroundColor:
                        "var(--surface-container-highest, #dcdcdc)",
                      borderRadius: 0,
                      p: "3px",
                      gap: "3px",
                    }}
                  >
                    {[
                      { key: "all", label: "All" },
                      { key: "1", label: "Open" },
                      { key: "0", label: "Paid" },
                      { key: "2", label: "Partial" },
                    ].map(({ key, label }) => (
                      <Box
                        key={key}
                        onClick={() => setInvoiceFilter(key)}
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 0,
                          cursor: "pointer",
                          backgroundColor:
                            invoiceFilter === key
                              ? "var(--surface-container-lowest, #ffffff)"
                              : "transparent",

                          transition: "all 0.15s ease",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: '"Brandon Grotesque", sans-serif',
                            fontSize: "0.5rem",
                            fontWeight: 700,
                            letterSpacing: "0.15em",
                            textTransform: "uppercase",
                            color:
                              invoiceFilter === key
                                ? "var(--on-surface, #131517)"
                                : "var(--on-surface-variant, #2a2b2d)",
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {activeTab !== TABS.BANK && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <input
                  type="search"
                  aria-label={
                    activeTab === TABS.INVOICES
                      ? "Search invoices"
                      : "Search transactions"
                  }
                  placeholder={
                    activeTab === TABS.INVOICES
                      ? "Search invoices"
                      : "Search transactions"
                  }
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  style={{ ...filterInputStyle, minWidth: 190 }}
                />
                <Box
                  component="span"
                  sx={{
                    display:
                      activeTab === TABS.INVOICES
                        ? { xs: "flex", md: "none" }
                        : "flex",
                  }}
                >
                  {activeTab === TABS.INVOICES ? (
                    <select
                      aria-label="Invoice status"
                      value={invoiceFilter}
                      onChange={(event) => setInvoiceFilter(event.target.value)}
                      style={filterInputStyle}
                    >
                      <option value="all">All statuses</option>
                      <option value="1">Open</option>
                      <option value="0">Paid</option>
                      <option value="2">Partial</option>
                    </select>
                  ) : (
                    <select
                      aria-label="Transaction status"
                      value={txnStatus}
                      onChange={(event) => setTxnStatus(event.target.value)}
                      style={filterInputStyle}
                    >
                      <option value="all">All statuses</option>
                      <option value="0">Paid</option>
                      <option value="1">Void</option>
                      <option value="2">Scheduled</option>
                      <option value="3">Canceled</option>
                    </select>
                  )}
                </Box>
                <input
                  type="date"
                  aria-label="From date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                  style={filterInputStyle}
                />
                <input
                  type="date"
                  aria-label="To date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                  style={filterInputStyle}
                />
                {(searchTerm || fromDate || toDate || txnStatus !== "all") && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchTerm("");
                      setFromDate("");
                      setToDate("");
                      setTxnStatus("all");
                      setInvoiceFilter("1");
                    }}
                  >
                    Clear
                  </Button>
                )}
                {activeTab === TABS.INVOICES && displayInvoices.length > 0 && (
                  <FormControlLabel
                    sx={{
                      m: 0,
                      ml: { xs: 0, sm: "auto" },
                      "& .MuiFormControlLabel-label": { fontSize: 12 },
                    }}
                    control={
                      <Checkbox
                        size="small"
                        checked={allSelected}
                        indeterminate={someSelected}
                        onChange={toggleSelectAll}
                        disabled={
                          fetchInvoicesStatus === "loading" ||
                          selectableInvoices.length === 0
                        }
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={
                      selectableInvoices.length > 0
                        ? `Select all open (${selectableInvoices.length})`
                        : "Select all open"
                    }
                  />
                )}
              </Box>
            )}

            {/* Invoices tab content */}
            {activeTab === TABS.INVOICES && (
              <Box
                sx={{
                  backgroundColor: "var(--surface-container-lowest, #ffffff)",
                  borderRadius: 0,
                  overflow: "hidden",
                }}
              >
                {fetchInvoicesStatus === "loading" ||
                fetchProjectsStatus === "loading" ? (
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {[1, 2].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        height={52}
                        sx={{ borderRadius: 0 }}
                      />
                    ))}
                  </Box>
                ) : fetchInvoicesStatus === "failed" ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    Failed to load invoices. Please try again.
                  </Alert>
                ) : displayInvoices.length === 0 ? (
                  <Typography
                    sx={{
                      fontFamily: '"Brandon Grotesque", sans-serif',
                      fontSize: "0.85rem",
                      color: "var(--on-surface-variant, #2a2b2d)",
                      p: 3,
                    }}
                  >
                    No invoices match the selected filter.
                  </Typography>
                ) : (
                  <>
                    {/* Header row */}
                    <Box
                      sx={{
                        display: { xs: "none", sm: "grid" },
                        gridTemplateColumns:
                          "40px 120px 1fr 120px 130px 90px 80px",
                        gap: 2,
                        px: 3,
                        py: 2,
                        backgroundColor: "rgba(242,244,244,0.4)",
                      }}
                    >
                      {[
                        "",
                        "Invoice #",
                        "Project",
                        "Date",
                        "Amount",
                        "Status",
                        "",
                      ].map((h) => (
                        <Typography
                          key={h || "action"}
                          sx={{
                            fontFamily: '"Brandon Grotesque", sans-serif',
                            fontSize: "0.5rem",
                            fontWeight: 700,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            color: "var(--outline, #737373)",
                          }}
                        >
                          {h}
                        </Typography>
                      ))}
                    </Box>
                    {/* Preview rows */}
                    {displayInvoices
                      .slice(0, PREVIEW_COUNT)
                      .map((inv: any, idx: number) => (
                        <InvoiceRow
                          key={inv.id || idx}
                          inv={inv}
                          onClick={handleInvoiceClick}
                          selectable={
                            !!displayProject &&
                            String(inv.paymentStatus) === "1"
                          }
                          checked={cartIds.includes(inv.id)}
                          onToggle={toggleCart}
                        />
                      ))}
                    {/* Expanded rows */}
                    <Collapse in={invoicesExpanded}>
                      {displayInvoices
                        .slice(PREVIEW_COUNT)
                        .map((inv: any, idx: number) => (
                          <InvoiceRow
                            key={inv.id || `exp-${idx}`}
                            inv={inv}
                            onClick={handleInvoiceClick}
                            selectable={
                              !!displayProject &&
                              String(inv.paymentStatus) === "1"
                            }
                            checked={cartIds.includes(inv.id)}
                            onToggle={toggleCart}
                          />
                        ))}
                    </Collapse>
                    <ExpandToggle
                      expanded={invoicesExpanded}
                      onClick={() => setInvoicesExpanded(!invoicesExpanded)}
                      totalCount={displayInvoices.length}
                      visibleCount={PREVIEW_COUNT}
                    />

                    {/* Multi-invoice cart: sticky bar and floating control */}
                    {cartInvoices.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          px: 3,
                          py: 1.5,
                          borderTop:
                            "1px solid var(--surface-container-high, #dcdcdc)",
                          backgroundColor:
                            "var(--surface-container-lowest, #ffffff)",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            color: "var(--on-surface, #131517)",
                          }}
                        >
                          {cartInvoices.length}{" "}
                          {cartInvoices.length === 1 ? "invoice" : "invoices"}{" "}
                          selected ·{" "}
                          {cartTotal.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                          })}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => setCartIds([])}>
                            Clear
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => setCartOpen(true)}
                            sx={{
                              fontFamily: '"Brandon Grotesque", sans-serif',
                              fontSize: "0.6rem",
                              fontWeight: 700,
                              letterSpacing: "0.15em",
                              textTransform: "uppercase",
                              backgroundColor: "var(--primary, #555759)",
                              color: "#ffffff",
                              borderRadius: 0,
                              "&:hover": {
                                backgroundColor: "var(--on-surface, #131517)",
                              },
                            }}
                          >
                            Pay selected
                          </Button>
                        </Box>
                      </Box>
                    )}
                    <CartFab
                      count={cartInvoices.length}
                      total={cartTotal}
                      onOpen={() => setCartOpen(true)}
                      onClear={() => setCartIds([])}
                    />
                    <CartPaymentDialog
                      open={cartOpen}
                      invoices={cartInvoices}
                      project={displayProject}
                      onClose={() => setCartOpen(false)}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Transactions tab content */}
            {activeTab === TABS.TRANSACTIONS && (
              <Box
                sx={{
                  backgroundColor: "var(--surface-container-lowest, #ffffff)",
                  borderRadius: 0,
                  overflow: "hidden",
                }}
              >
                {fetchTransactionsStatus === "loading" ? (
                  <Box
                    sx={{
                      p: 3,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                    }}
                  >
                    {[1, 2].map((i) => (
                      <Skeleton
                        key={i}
                        variant="rounded"
                        height={52}
                        sx={{ borderRadius: 0 }}
                      />
                    ))}
                  </Box>
                ) : fetchTransactionsStatus === "failed" ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    Failed to load transactions. Please try again.
                  </Alert>
                ) : displayTransactions.length === 0 ? (
                  <Typography
                    sx={{
                      fontFamily: '"Brandon Grotesque", sans-serif',
                      fontSize: "0.85rem",
                      color: "var(--on-surface-variant, #2a2b2d)",
                      p: 3,
                    }}
                  >
                    No transactions on record.
                  </Typography>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: { xs: "none", sm: "grid" },
                        gridTemplateColumns:
                          "1fr 1fr 120px 100px 120px 90px",
                        gap: 2,
                        px: 3,
                        py: 2,
                        backgroundColor: "rgba(242,244,244,0.4)",
                      }}
                    >
                      {[
                        "Project",
                        "Invoice #",
                        "Payment Date",
                        "Method",
                        "Amount",
                        "Status",
                      ].map((h) => (
                        <Typography
                          key={h}
                          sx={{
                            fontFamily: '"Brandon Grotesque", sans-serif',
                            fontSize: "0.5rem",
                            fontWeight: 700,
                            letterSpacing: "0.2em",
                            textTransform: "uppercase",
                            color: "var(--outline, #737373)",
                          }}
                        >
                          {h}
                        </Typography>
                      ))}
                    </Box>
                    {displayTransactions
                      .slice(0, PREVIEW_COUNT)
                      .map((txn: any, idx: number) => (
                        <TransactionRow
                          key={txn.id || idx}
                          txn={txn}
                          onClick={setSelectedTxn}
                        />
                      ))}
                    <Collapse in={invoicesExpanded}>
                      {displayTransactions
                        .slice(PREVIEW_COUNT)
                        .map((txn: any, idx: number) => (
                          <TransactionRow
                            key={txn.id || `exp-${idx}`}
                            txn={txn}
                            onClick={setSelectedTxn}
                          />
                        ))}
                    </Collapse>
                    <ExpandToggle
                      expanded={invoicesExpanded}
                      onClick={() => setInvoicesExpanded(!invoicesExpanded)}
                      totalCount={displayTransactions.length}
                      visibleCount={PREVIEW_COUNT}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Bank details tab content */}
            {activeTab === TABS.BANK && (
              <Box
                sx={{
                  backgroundColor: "var(--surface-container-lowest, #ffffff)",
                  borderRadius: 0,
                  p: 3,
                }}
              >
                {displayProject ? (
                  <>
                    {bankAccounts.length ? (
                      bankAccounts.map((account: any, index: number) => (
                        <Box
                          key={account?.accountID || index}
                          sx={{ py: 1, borderBottom: "1px solid #eeeeee" }}
                        >
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {account?.accountName || "Bank account"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {maskBankAccount(account?.accountNumber)} ·{" "}
                            {bankAccountType(account)}
                          </Typography>
                          {account?.addedAt && (
                            <Typography variant="caption" color="text.secondary">
                              Added {fmtDate(account.addedAt)}
                            </Typography>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No bank account linked. Add one to make payments.
                      </Typography>
                    )}
                    <Button
                      size="small"
                      variant={bankAccounts.length ? "outlined" : "contained"}
                      sx={{ mt: 1.5, borderRadius: 0 }}
                      onClick={() => dispatch(openProjectDrawer(displayProject))}
                    >
                      {bankAccounts.length
                        ? "Manage bank account"
                        : "Add bank account"}
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Select a project to see its bank details.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* ══════ RIGHT COLUMN ══════ */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 4, md: 5 },
          }}
        >
          {/* ═══ Financial Library ═══ */}
          <Box
            sx={{
              backgroundColor: "var(--surface-container-lowest, #ffffff)",
              border: "1px solid var(--tpc-200)",
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.55rem",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "var(--on-surface, #131517)",
                mb: 2.5,
              }}
            >
              Financial Library
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {FINANCIAL_LINKS.map((link) => (
                <LibraryRow
                  key={link.key}
                  label={link.label}
                  icon={FolderZipIcon}
                  onClick={() => handleLibraryLinkClick(link)}
                />
              ))}
            </Box>
          </Box>

          {/* ═══ Project Image ═══ */}
          {displayProject?.backgroundImage && (
            <Box
              sx={{
                position: "relative",
                borderRadius: 0,
                overflow: "hidden",
                height: 200,
              }}
            >
              <Box
                component="img"
                src={displayProject.backgroundImage}
                alt={displayProject.name || "Project"}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 0.7s ease",
                  "&:hover": { transform: "scale(1.04)" },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(to top, rgba(45,52,53,0.55) 0%, transparent 50%)",
                  pointerEvents: "none",
                }}
              />
              <Box sx={{ position: "absolute", bottom: 16, left: 20 }}>
                <Typography
                  sx={{
                    fontFamily: '"Brandon Grotesque", sans-serif',
                    fontSize: "0.5rem",
                    fontWeight: 700,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.7)",
                  }}
                >
                  Active Project
                </Typography>
                <Typography
                  sx={{
                    fontFamily: '"Brandon Grotesque", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    color: "#ffffff",
                  }}
                >
                  {displayProject.name}
                </Typography>
              </Box>
            </Box>
          )}

          {/* ═══ Help CTA ═══ */}
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 0,
              border: "1px solid var(--tpc-200)",
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontWeight: 700,
                fontSize: "0.85rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "var(--on-surface, #131517)",
                mb: 1,
              }}
            >
              Need financial clarity?
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.7rem",
                color: "var(--on-surface-variant, #2a2b2d)",
                lineHeight: 1.6,
              }}
            >
              Your project team is available for a detailed walkthrough of your
              budget, change orders, and draw schedule.
            </Typography>
          </Box>
        </Box>
      </Box>

      <InvoiceDetailsDrawer />

      <Dialog
        open={!!selectedTxn}
        onClose={() => setSelectedTxn(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 0 } }}
      >
        <DialogTitle>Transaction details</DialogTitle>
        <DialogContent dividers>
          {selectedTxn && (
            <Box sx={{ display: "grid", gap: 1.25 }}>
              {[
                ["Project", selectedTxn.projectName],
                [
                  "Payment date",
                  fmtDate(selectedTxn.paymentDate || selectedTxn.createdTime),
                ],
                ["Method", txnMethod(selectedTxn)],
                ["Amount", fmtCurrency(selectedTxn.amount)],
                [
                  "Status",
                  TXN_STATUS_LABEL[String(selectedTxn.status)] || "Unknown",
                ],
                ["Reference", selectedTxn.referenceNumber],
              ]
                .filter(([, value]) => value)
                .map(([label, value]) => (
                  <Box key={label as string}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: "var(--outline, #737373)",
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography sx={{ fontSize: "0.9rem" }}>
                      {String(value)}
                    </Typography>
                  </Box>
                ))}

              {Array.isArray(selectedTxn.invoicePayments) &&
              selectedTxn.invoicePayments.length > 0 ? (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "var(--outline, #737373)",
                      mb: 0.5,
                    }}
                  >
                    Invoices paid
                  </Typography>
                  {selectedTxn.invoicePayments.map(
                    (line: any, index: number) => (
                      <Typography
                        key={line.invoiceId || line.invoiceNumber || index}
                        sx={{ fontSize: "0.9rem" }}
                      >
                        {line.invoiceNumber || line.invoiceId || "Invoice"} ·{" "}
                        {fmtCurrency(line.amount)}
                      </Typography>
                    ),
                  )}
                </Box>
              ) : selectedTxn.invoiceNumber ? (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "var(--outline, #737373)",
                    }}
                  >
                    Invoice
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    {selectedTxn.invoiceNumber}
                  </Typography>
                </Box>
              ) : null}

              {selectedTxn.description && (
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      color: "var(--outline, #737373)",
                    }}
                  >
                    Description
                  </Typography>
                  <Typography sx={{ fontSize: "0.9rem" }}>
                    {selectedTxn.description}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedTxn(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────

const InvoiceRow = ({
  inv,
  onClick,
  selectable,
  checked,
  onToggle,
}: {
  inv: any;
  onClick: (inv: any) => void;
  selectable?: boolean;
  checked?: boolean;
  onToggle?: (invoiceId: string) => void;
}) => {
  const isOpen = String(inv.paymentStatus) === "1";
  const processing = !!inv.paymentProcessing;
  const statusKey = processing ? "processing" : String(inv.paymentStatus);
  const canSelect = !!selectable && !processing && invoicePayable(inv) > 0;
  return (
    <Box
      onClick={() => onClick(inv)}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "auto 1fr auto",
          sm: "40px 120px 1fr 120px 130px 90px 80px",
        },
        gap: 2,
        py: 2,
        px: 3,
        alignItems: "center",
        cursor: "pointer",
        transition: "background 0.15s ease",
        "&:hover": { backgroundColor: "var(--surface-container-low, #f7f7f7)" },
      }}
    >
      <Checkbox
        size="small"
        checked={!!checked}
        disabled={!canSelect}
        onClick={(e) => e.stopPropagation()}
        onChange={() => canSelect && onToggle?.(inv.id)}
        sx={{
          p: 0.5,
          color: canSelect ? "var(--primary, #555759)" : undefined,
        }}
      />
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontWeight: 600,
          fontSize: "0.8rem",
          color: "var(--on-surface, #131517)",
        }}
      >
        {inv.invoiceNumber}
      </Typography>
      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontSize: "0.8rem",
          color: "var(--on-surface-variant, #2a2b2d)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {inv.projectName || "—"}
      </Typography>
      <Typography
        sx={{
          display: { xs: "none", sm: "block" },
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontSize: "0.8rem",
          color: "var(--on-surface-variant, #2a2b2d)",
        }}
      >
        {inv.invoiceDate ? fmtDate(inv.invoiceDate) : "—"}
      </Typography>
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontWeight: 700,
          fontSize: "0.85rem",
          color: "var(--on-surface, #131517)",
        }}
      >
        {fmtCurrency(inv.amount)}
      </Typography>
      <Chip
        label={STATUS_LABEL[statusKey] || "Unknown"}
        size="small"
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontSize: "0.45rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          backgroundColor:
            STATUS_BG[statusKey] || "var(--surface-container, #efefef)",
          color:
            STATUS_COLOR[statusKey] || "var(--on-surface, #131517)",
          border: "none",
          height: 22,
          borderRadius: 0,
        }}
      />
      {/* Pay Now button — only for open invoices, and not while a debit clears */}
      <Box
        sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "flex-end" }}
      >
        {isOpen && !processing ? (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onClick(inv);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.5,
              py: 0.6,
              borderRadius: 0,
              backgroundColor: "var(--primary, #555759)",
              cursor: "pointer",
              transition: "all 0.15s ease",
              "&:hover": {
                backgroundColor: "var(--on-surface, #131517)",
                transform: "translateY(-1px)",
              },
            }}
          >
            <PaymentIcon sx={{ fontSize: 12, color: "#ffffff" }} />
            <Typography
              sx={{
                fontFamily: '"Brandon Grotesque", sans-serif',
                fontSize: "0.4rem",
                fontWeight: 700,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#ffffff",
              }}
            >
              Pay
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: 52 }} /> // spacer for alignment
        )}
      </Box>
    </Box>
  );
};

const TransactionRow = ({
  txn,
  onClick,
}: {
  txn: any;
  onClick?: (txn: any) => void;
}) => (
  <Box
    onClick={() => onClick?.(txn)}
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "1fr auto",
        sm: "1fr 1fr 120px 100px 120px 90px",
      },
      gap: 2,
      py: 2,
      px: 3,
      alignItems: "center",
      cursor: "pointer",
      transition: "background 0.15s ease",
      "&:hover": { backgroundColor: "var(--surface-container-low, #f7f7f7)" },
    }}
  >
    <Typography
      sx={{
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontWeight: 600,
        fontSize: "0.8rem",
        color: "var(--on-surface, #131517)",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {txn.projectName || "—"}
    </Typography>
    <Typography
      sx={{
        display: { xs: "none", sm: "block" },
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontSize: "0.8rem",
        color: "var(--on-surface-variant, #2a2b2d)",
      }}
    >
      {txn.invoiceNumber ||
        (txn.invoicePayments?.length
          ? `${txn.invoicePayments.length} invoice${
              txn.invoicePayments.length === 1 ? "" : "s"
            }`
          : "—")}
    </Typography>
    <Typography
      sx={{
        display: { xs: "none", sm: "block" },
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontSize: "0.8rem",
        color: "var(--on-surface-variant, #2a2b2d)",
      }}
    >
      {txn.paymentDate || txn.createdTime
        ? fmtDate(txn.paymentDate || txn.createdTime)
        : "—"}
    </Typography>
    <Typography
      sx={{
        display: { xs: "none", sm: "block" },
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontSize: "0.8rem",
        color: "var(--on-surface-variant, #2a2b2d)",
      }}
    >
      {txnMethod(txn)}
    </Typography>
    <Typography
      sx={{
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontWeight: 700,
        fontSize: "0.85rem",
        color: "var(--on-surface, #131517)",
      }}
    >
      {fmtCurrency(txn.amount)}
    </Typography>
    <Chip
      label={TXN_STATUS_LABEL[String(txn.status)] || "Unknown"}
      size="small"
      sx={{
        fontFamily: '"Brandon Grotesque", sans-serif',
        fontSize: "0.45rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        backgroundColor: "var(--surface-container, #efefef)",
        color: "var(--on-surface, #131517)",
        border: "none",
        height: 22,
        borderRadius: 0,
      }}
    />
  </Box>
);

const LibraryRow = ({
  label,
  icon: Icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: any;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <Box
    onClick={disabled ? undefined : onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2.5,
      py: 2,
      backgroundColor: "var(--surface-container-lowest, #ffffff)",
      border: "1px solid var(--tpc-200)",
      borderRadius: 0,
      cursor: disabled ? "default" : "pointer",
      transition: "all 0.15s ease",
      opacity: disabled ? 0.45 : 1,
      "&:hover": disabled ? {} : { borderColor: "var(--tpc-600)" },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Icon sx={{ fontSize: 18, color: "var(--primary, #555759)" }} />
      <Typography
        sx={{
          fontFamily: '"Brandon Grotesque", sans-serif',
          fontWeight: 700,
          fontSize: "0.65rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--on-surface, #131517)",
        }}
      >
        {label}
      </Typography>
    </Box>
    <ChevronRightIcon sx={{ fontSize: 18, color: "var(--outline, #737373)" }} />
  </Box>
);

export default FinancialDashboard;
